/**
 * The Help text, as data.
 *
 * Source: `Pomoc v.2.docx`, sent by the client on 2026-09-18. Her requirements
 * were that the app's own icons appear where the document writes `[ikonka]`,
 * that the markup be accessible, and that the text be rendered with the app's own
 * reading settings rather than some separate document style.
 *
 * Data rather than JSX or Markdown, for two reasons. An icon key can resolve to
 * the real toolbar component, so Help shows the same glyph the reader is being
 * told to look for instead of a picture of one. And her wording stays in a single
 * file that can be re-pasted when she revises it, rather than being scattered
 * through markup.
 *
 * ---
 *
 * ADAPTED, ON INSTRUCTION. The client's direction on 2026-09-22 was explicit:
 * do not change the app to match this description, change the description to
 * match the app. Every claim below was checked against the code, and the
 * following were wrong in the source document:
 *
 * - Section 9 offered a choice of reader voice. DYS-044 removed the voice picker
 *   on the client's own instruction, so the app now chooses the best Polish voice
 *   itself. The clause is gone and the automatic choice is explained instead.
 * - Tap-to-read (DYS-045) did not exist when she wrote this. It is section 9 now.
 * - Section 10 promised the file lands where the reader chooses. True on a
 *   desktop since DYS-043, false on a phone, where Chromium will not share a
 *   .docx and no save dialogue exists (DYS-049). Both cases are stated.
 * - The download control was called "Pobierz". The app labels it "Eksportuj".
 * - Section 5 said "rozpocznij przetwarzanie". The button says "Rozpoznaj tekst".
 * - The two lists of accepted formats contradicted each other (JPG/PNG in one
 *   place, JPEG/PNG/WebP/GIF in another). Both now match the upload control.
 * - Section 7 carried an unanswered comment from the author asking what the
 *   "Edytuj" icon does. It is answered from what the code actually does.
 * - A stray "edytuj" line at the end of section 10 has gone.
 * - Heading levels disagreed between sections 3, 11 and 12. Every sub-heading is
 *   one level below its section here, which is the point of the `subsections`
 *   field rather than a flag on each block.
 *
 * Keep it that way: when the app changes, this file changes with it.
 */

/** Which toolbar icon stands beside a step. Resolved to a component by the view. */
export type HelpIconKey = 'camera' | 'file' | 'edit' | 'customise' | 'speaker' | 'export'

export type HelpBlock =
  /** Ordinary prose. */
  | { kind: 'paragraph'; text: string }
  /** A bulleted list. Parallel items only - this is not a way to make paragraphs. */
  | { kind: 'list'; items: string[] }
  /**
   * A control the reader is being told to press: its icon, its label exactly as
   * the app spells it, and what it does. The label is quoted in the app's own
   * words on purpose - help that renames a button is worse than no help.
   */
  | { kind: 'action'; icon: HelpIconKey; label: string; text: string }
  /** Something that will cost the reader if they miss it. Set apart visually. */
  | { kind: 'note'; text: string }

export interface HelpSubsection {
  /** Rendered one heading level below its section. */
  heading: string
  blocks: HelpBlock[]
}

export interface HelpSection {
  /** Stable, used for the heading id and the contents links. */
  id: string
  heading: string
  /** Shown beside the heading where the source document wrote `[ikonka]`. */
  icon?: HelpIconKey
  blocks: HelpBlock[]
  subsections?: HelpSubsection[]
}

export const HELP_TITLE = 'Pomoc'

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'czym-jest-dyslexa',
    heading: '1. Czym jest Dyslexa?',
    blocks: [
      { kind: 'paragraph', text: 'Dyslexa to aplikacja, która pomaga w czytaniu tekstów.' },
      {
        kind: 'paragraph',
        text: 'Możesz zrobić zdjęcie strony z książki lub innego dokumentu. Możesz też wgrać gotowy plik. Dyslexa rozpozna znajdujący się w nim tekst i pokaże go w formie, którą łatwiej czytać.',
      },
      {
        kind: 'paragraph',
        text: 'Możesz dostosować wygląd tekstu do swoich potrzeb. Możesz na przykład powiększyć litery, zmienić czcionkę, odstępy między wierszami oraz kolory liter i tła.',
      },
      { kind: 'paragraph', text: 'Jeśli wolisz słuchać zamiast czytać, Dyslexa może przeczytać tekst na głos.' },
      { kind: 'paragraph', text: 'Z aplikacji mogą korzystać między innymi:' },
      { kind: 'list', items: ['osoby z dysleksją,', 'ich rodzice,', 'oraz nauczyciele.'] },
      {
        kind: 'paragraph',
        text: 'Dyslexa może być pomocna też dla osób z innymi trudnościami w czytaniu (na przykład spowodowanymi wadami wzroku).',
      },
      {
        kind: 'paragraph',
        text: 'Nie musisz instalować programu. Dyslexa działa w przeglądarce internetowej na komputerze, laptopie, tablecie i smartfonie. Wystarczy, że wpiszesz w przeglądarkę adres: dyslexa.pl',
      },
    ],
  },
  {
    id: 'jak-zaczac',
    heading: '2. Jak zacząć?',
    blocks: [
      { kind: 'paragraph', text: 'Na początek wgraj tekst. Masz dwie możliwości:' },
      {
        kind: 'action',
        icon: 'camera',
        label: 'Otwórz aparat',
        text: 'wybierz tę opcję, jeśli chcesz zrobić zdjęcie tekstu.',
      },
      {
        kind: 'action',
        icon: 'file',
        label: 'Wybierz plik',
        text: 'wybierz tę opcję, jeśli masz już zdjęcie lub dokument zapisany na urządzeniu.',
      },
      { kind: 'paragraph', text: 'Dyslexa obsługuje:' },
      {
        kind: 'list',
        items: ['zdjęcia i obrazy (JPG, PNG),', 'pliki PDF,', 'dokumenty Word (DOCX).'],
      },
      { kind: 'paragraph', text: 'Plik może mieć maksymalnie 10 MB.' },
    ],
  },
  {
    id: 'czego-potrzebujesz',
    heading: '3. Czego potrzebujesz, aby korzystać z Dyslexy?',
    blocks: [
      { kind: 'paragraph', text: 'Dyslexa działa na:' },
      { kind: 'list', items: ['komputerze,', 'laptopie,', 'tablecie,', 'smartfonie.'] },
      {
        kind: 'paragraph',
        text: 'Potrzebujesz nowoczesnej przeglądarki, na przykład Chrome, Firefox, Safari lub Edge w aktualnej wersji.',
      },
      { kind: 'paragraph', text: 'Nie musisz instalować Dyslexy na swoim urządzeniu.' },
    ],
    subsections: [
      {
        heading: 'Jeśli chcesz robić zdjęcia',
        blocks: [
          { kind: 'paragraph', text: 'Twoje urządzenie musi mieć aparat.' },
          {
            kind: 'paragraph',
            text: 'Przy pierwszym użyciu tej funkcji przeglądarka może zapytać, czy pozwalasz Dyslexie korzystać z aparatu. Wyraź zgodę, jeśli chcesz robić zdjęcia bezpośrednio w aplikacji.',
          },
          {
            kind: 'paragraph',
            text: 'Aparat nie jest potrzebny, jeśli korzystasz z plików, które są już zapisane na Twoim urządzeniu.',
          },
        ],
      },
      {
        heading: 'Jeśli chcesz słuchać tekstu',
        blocks: [
          { kind: 'paragraph', text: 'Potrzebujesz głośników lub słuchawek.' },
          { kind: 'paragraph', text: 'Dźwięk musi być włączony na Twoim urządzeniu.' },
          {
            kind: 'paragraph',
            text: 'Czytanie na głos działa najlepiej w przeglądarkach Chrome i Edge. W innych przeglądarkach może być niedostępne — Dyslexa powie Ci o tym, gdy spróbujesz je włączyć.',
          },
        ],
      },
      {
        heading: 'Połączenie z internetem',
        blocks: [
          { kind: 'paragraph', text: 'Dyslexa najlepiej rozpoznaje tekst, gdy masz połączenie z internetem.' },
          {
            kind: 'paragraph',
            text: 'Jeśli połączenia nie ma lub usługa rozpoznawania tekstu nie działa, aplikacja może automatycznie skorzystać z trybu awaryjnego.',
          },
          { kind: 'paragraph', text: 'Tryb awaryjny działa jednak słabiej niż rozpoznawanie tekstu przez internet.' },
        ],
      },
    ],
  },
  {
    id: 'zrob-zdjecie',
    heading: '4. Chcę zrobić zdjęcie tekstu',
    blocks: [
      {
        kind: 'action',
        icon: 'camera',
        label: 'Otwórz aparat',
        text: 'wybierz tę opcję na stronie startowej.',
      },
      {
        kind: 'paragraph',
        text: 'Przy pierwszym użyciu przeglądarka może zapytać, czy pozwalasz Dyslexie korzystać z aparatu. Musisz wyrazić zgodę, aby zrobić zdjęcie.',
      },
      { kind: 'paragraph', text: 'Ustaw aparat tak, aby cały tekst był dobrze widoczny. Postaraj się, aby:' },
      {
        kind: 'list',
        items: [
          'zdjęcie było ostre,',
          'na stronie było wystarczająco dużo światła,',
          'tekst nie był zasłonięty,',
          'aparat znajdował się możliwie prosto nad tekstem.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'Zrób zdjęcie. Zobaczysz jego podgląd. Jeśli zdjęcie będzie słabej jakości, Dyslexa poinformuje Cię o tym i będziesz mieć możliwość zrobić lepsze.',
      },
      {
        kind: 'paragraph',
        text: 'W razie potrzeby możesz obrócić obraz przyciskiem „Obróć” albo usunąć zdjęcie i zrobić je ponownie.',
      },
      {
        kind: 'paragraph',
        text: 'Jeżeli chcesz przetworzyć więcej stron, wybierz „Dodaj kolejne zdjęcie”.',
      },
      {
        kind: 'paragraph',
        text: 'Gdy zdjęcia są gotowe, wybierz „Rozpoznaj tekst”.',
      },
    ],
  },
  {
    id: 'mam-plik',
    heading: '5. Mam już zdjęcie lub dokument',
    blocks: [
      {
        kind: 'action',
        icon: 'file',
        label: 'Wybierz plik',
        text: 'wybierz tę opcję na stronie startowej, a następnie znajdź plik na swoim urządzeniu.',
      },
      {
        kind: 'paragraph',
        text: 'Dyslexa może pracować między innymi ze zdjęciami stron z książek oraz dokumentami PDF. Możesz wgrać również dokument Word w formacie DOCX.',
      },
      {
        kind: 'paragraph',
        text: 'Jeżeli wgrasz wielostronicowy PDF, Dyslexa będzie przetwarzać jego kolejne strony.',
      },
      {
        kind: 'paragraph',
        text: 'Przed rozpoczęciem sprawdź podgląd dokumentu. Jeżeli wszystko się zgadza, wybierz „Rozpoznaj tekst”.',
      },
    ],
  },
  {
    id: 'rozpoznawanie',
    heading: '6. Poczekaj na rozpoznanie tekstu',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Po rozpoczęciu przetwarzania Dyslexa rozpoznaje tekst znajdujący się na zdjęciu lub w dokumencie.',
      },
      { kind: 'paragraph', text: 'Na ekranie zobaczysz informację o postępie pracy.' },
      { kind: 'paragraph', text: 'Przy większej liczbie stron może to potrwać dłużej. Nie musisz nic robić.' },
      {
        kind: 'paragraph',
        text: 'Jeśli nie chcesz kontynuować, wybierz „Anuluj przetwarzanie”.',
      },
      { kind: 'paragraph', text: 'Po zakończeniu Dyslexa pokaże rozpoznany tekst.' },
      {
        kind: 'note',
        text: 'Automatyczne rozpoznawanie tekstu nie zawsze jest idealne. Trudności mogą powodować na przykład niewyraźne zdjęcia, nietypowy układ strony, tabele lub słaba jakość oryginału. Dlatego warto sprawdzić rozpoznany tekst, szczególnie jeśli korzystasz z niego do nauki lub pracy.',
      },
    ],
  },
  {
    id: 'edytuj',
    heading: '7. Edytuj tekst',
    icon: 'edit',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Ikona ołówka na ekranie z rozpoznanym tekstem otwiera tekst do edycji. Możesz wtedy pisać w nim tak jak w zwykłym edytorze.',
      },
      {
        kind: 'paragraph',
        text: 'Przydaje się to przede wszystkim wtedy, gdy rozpoznawanie pomyliło literę lub słowo — możesz je po prostu poprawić.',
      },
      {
        kind: 'paragraph',
        text: 'Możesz też dopisać własny tekst, na przykład polecenie do ćwiczenia. Bywa to przydatne dla nauczycieli i rodziców, którzy przygotowują materiał dla kogoś innego.',
      },
      {
        kind: 'paragraph',
        text: 'Gdy skończysz, wybierz „Zapisz zmiany”. Możesz też wybrać „Anuluj”, aby wrócić do tekstu sprzed edycji.',
      },
      {
        kind: 'note',
        text: 'Poprawki wprowadzasz tylko w Dyslexie. Oryginalne zdjęcie ani oryginalny plik się nie zmieniają.',
      },
    ],
  },
  {
    id: 'dostosuj',
    heading: '8. Dostosuj tekst do siebie',
    icon: 'customise',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Nie istnieje jeden sposób wyświetlania tekstu, który jest najlepszy dla każdej osoby z dysleksją.',
      },
      {
        kind: 'paragraph',
        text: 'Dlatego w Dyslexie możesz samodzielnie wybrać ustawienia, przy których czyta Ci się najwygodniej. Otwórz panel „Dostosuj” na ekranie z rozpoznanym tekstem.',
      },
      { kind: 'paragraph', text: 'Możesz zmienić:' },
      {
        kind: 'paragraph',
        text: 'Czcionka — wypróbuj dostępne czcionki i wybierz tę, która jest dla Ciebie najbardziej czytelna.',
      },
      { kind: 'paragraph', text: 'Rozmiar — jeżeli litery są za małe, powiększ je.' },
      {
        kind: 'paragraph',
        text: 'Odstęp — możesz zwiększyć przestrzeń między kolejnymi wierszami. Dzięki temu tekst może wydawać się mniej „ściśnięty”.',
      },
      {
        kind: 'paragraph',
        text: 'Kolory — nie każdemu najlepiej czyta się czarny tekst na białym tle. Wybierz jeden z dziewięciu gotowych zestawów kolorów tekstu i tła — ten, który najmniej męczy Twój wzrok.',
      },
      {
        kind: 'paragraph',
        text: 'Każde zdanie w osobnej linii — możesz włączyć opcję, która rozpoczyna każde zdanie w nowej linii. Sprawdź, czy taki układ ułatwia Ci śledzenie tekstu.',
      },
      {
        kind: 'paragraph',
        text: 'Nie musisz zmieniać wszystkich ustawień. Zmieniaj tylko te, które rzeczywiście pomagają Ci w czytaniu.',
      },
      {
        kind: 'paragraph',
        text: 'Dyslexa zapamięta Twoje ustawienia i następnym razem pokaże Ci tekst w taki sam sposób. Zawsze możesz je zmienić.',
      },
    ],
  },
  {
    id: 'posluchaj',
    heading: '9. Posłuchaj tekstu',
    icon: 'speaker',
    blocks: [
      { kind: 'paragraph', text: 'Nie musisz czytać całego tekstu samodzielnie. Dyslexa może przeczytać go na głos.' },
      {
        kind: 'action',
        icon: 'speaker',
        label: 'Odtwórz',
        text: 'wybierz tę ikonę na ekranie z rozpoznanym tekstem, aby rozpocząć czytanie.',
      },
      {
        kind: 'paragraph',
        text: 'Czytany fragment jest podświetlany, dzięki czemu łatwiej śledzić tekst wzrokiem.',
      },
      {
        kind: 'paragraph',
        text: 'Gdy czytanie się rozpocznie, na dole ekranu pojawi się pasek sterowania. Możesz w nim zatrzymać czytanie i wznowić je, przejść do poprzedniego lub następnego fragmentu oraz czytać wolniej albo szybciej.',
      },
      {
        kind: 'paragraph',
        text: 'Gdy czytanie trwa, możesz też dotknąć dowolnego zdania w tekście, a Dyslexa zacznie czytać od tego miejsca.',
      },
      {
        kind: 'paragraph',
        text: 'Możesz słuchać tekstu przez głośniki urządzenia lub słuchawki.',
      },
      {
        kind: 'note',
        text: 'Dyslexa czyta po polsku i sama wybiera najlepszy dostępny głos na Twoim urządzeniu. Brzmienie głosu zależy więc od urządzenia i przeglądarki, z których korzystasz.',
      },
    ],
  },
  {
    id: 'zapisz',
    heading: '10. Zapisz tekst',
    icon: 'export',
    blocks: [
      { kind: 'paragraph', text: 'Rozpoznany tekst możesz zapisać jako dokument Word (DOCX).' },
      {
        kind: 'action',
        icon: 'export',
        label: 'Eksportuj',
        text: 'wybierz tę ikonę na ekranie z rozpoznanym tekstem.',
      },
      {
        kind: 'paragraph',
        text: 'Na komputerze w przeglądarce Chrome lub Edge pojawi się okno, w którym wskażesz miejsce zapisu. W pozostałych przeglądarkach oraz na telefonie i tablecie plik trafia do folderu Pobrane.',
      },
      {
        kind: 'paragraph',
        text: 'Zapisany plik możesz później otworzyć w programie obsługującym dokumenty Word. Dzięki temu możesz na przykład:',
      },
      {
        kind: 'list',
        items: [
          'zachować materiał do późniejszej nauki,',
          'poprawić lub uzupełnić tekst,',
          'przesłać dokument innej osobie,',
          'wydrukować go.',
        ],
      },
      {
        kind: 'paragraph',
        text: 'To może być przydatne również dla rodziców i nauczycieli przygotowujących materiały dla osoby z dysleksją.',
      },
      {
        kind: 'note',
        text: 'Jeśli nie zapiszesz pliku na swoim urządzeniu, rozpoznany tekst zostanie utracony. Dyslexa wyświetli Ci wcześniej ostrzeżenie: „Czy na pewno chcesz wyjść?”.',
      },
    ],
  },
  {
    id: 'przyklady',
    heading: '11. Przykłady korzystania z Dyslexy',
    blocks: [],
    subsections: [
      {
        heading: 'Uczę się z podręcznika',
        blocks: [
          { kind: 'paragraph', text: 'Zrób zdjęcie strony lub stron z podręcznika.' },
          { kind: 'paragraph', text: 'Poczekaj, aż Dyslexa rozpozna tekst.' },
          { kind: 'paragraph', text: 'Ustaw taki wygląd tekstu, przy którym czyta Ci się najlepiej.' },
          { kind: 'paragraph', text: 'Teraz możesz przeczytać materiał albo go posłuchać.' },
          {
            kind: 'paragraph',
            text: 'Możesz go też zapisać, przesłać na inne urządzenie lub wydrukować, a także wymieniać się plikami ze znajomymi.',
          },
        ],
      },
      {
        heading: 'Dostałem materiał w PDF',
        blocks: [
          { kind: 'paragraph', text: 'Wybierz „Wybierz plik” i dodaj PDF.' },
          { kind: 'paragraph', text: 'Poczekaj na przetworzenie stron.' },
          { kind: 'paragraph', text: 'Po rozpoznaniu tekstu dostosuj jego wygląd lub uruchom czytanie na głos.' },
          {
            kind: 'paragraph',
            text: 'Możesz go też zapisać, przesłać na inne urządzenie lub wydrukować.',
          },
        ],
      },
      {
        heading: 'Jestem rodzicem',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Możesz wgrać materiał, z którego uczy się Twoje dziecko, a następnie dostosować jego wygląd do potrzeb dziecka.',
          },
          {
            kind: 'paragraph',
            text: 'Gotowy tekst możesz zapisać jako dokument Word. Możesz go przesłać dziecku lub wydrukować.',
          },
        ],
      },
      {
        heading: 'Jestem nauczycielem',
        blocks: [
          { kind: 'paragraph', text: 'Możesz przetworzyć zdjęcie, skan lub PDF z materiałem edukacyjnym.' },
          {
            kind: 'paragraph',
            text: 'Następnie możesz przygotować bardziej czytelną wersję tekstu i zapisać ją jako dokument Word.',
          },
        ],
      },
    ],
  },
  {
    id: 'problemy',
    heading: '12. Co zrobić, gdy coś nie działa?',
    blocks: [],
    subsections: [
      {
        heading: 'Nie mogę zrobić zdjęcia',
        blocks: [
          { kind: 'paragraph', text: 'Sprawdź, czy urządzenie ma aparat.' },
          { kind: 'paragraph', text: 'Sprawdź również, czy przeglądarka ma pozwolenie na korzystanie z aparatu.' },
          {
            kind: 'paragraph',
            text: 'Jeśli wcześniej nie wyraziłeś zgody, może być konieczna zmiana ustawień przeglądarki.',
          },
        ],
      },
      {
        heading: 'Nie mogę wgrać pliku',
        blocks: [
          { kind: 'paragraph', text: 'Sprawdź format i wielkość pliku.' },
          { kind: 'paragraph', text: 'Dyslexa obsługuje zdjęcia i obrazy (JPG, PNG), pliki PDF oraz dokumenty Word (DOCX).' },
          { kind: 'paragraph', text: 'Maksymalny rozmiar pliku to 10 MB.' },
        ],
      },
      {
        heading: 'Rozpoznany tekst zawiera błędy',
        blocks: [
          { kind: 'paragraph', text: 'Spróbuj zrobić nowe zdjęcie.' },
          { kind: 'paragraph', text: 'Zadbaj o dobre światło i ostrość. Ustaw aparat prosto nad stroną.' },
          {
            kind: 'paragraph',
            text: 'Drobne pomyłki możesz poprawić samodzielnie — otwórz tekst do edycji ikoną ołówka.',
          },
        ],
      },
      {
        heading: 'Czytanie na głos nie działa',
        blocks: [
          { kind: 'paragraph', text: 'Sprawdź, czy dźwięk w urządzeniu jest włączony.' },
          { kind: 'paragraph', text: 'Jeżeli używasz słuchawek, sprawdź, czy są prawidłowo podłączone.' },
          {
            kind: 'paragraph',
            text: 'Czytanie na głos zależy też od przeglądarki. Działa najlepiej w Chrome i Edge.',
          },
        ],
      },
      {
        heading: 'Przetwarzanie zostało przerwane',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Jeżeli dokument ma wiele stron, Dyslexa zapisuje informacje o postępie przetwarzania. Dzięki temu po odświeżeniu strony można wznowić pracę.',
          },
        ],
      },
      {
        heading: 'W tekście są błędy, których nie było w oryginale',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Dyslexa korzysta z automatycznego rozpoznawania tekstu opartego o sztuczną inteligencję. Może ono czasem pomylić literę, słowo lub fragment dokumentu.',
          },
          {
            kind: 'paragraph',
            text: 'Dlatego warto porównać rozpoznany tekst z oryginałem, zanim wykorzystasz go do nauki lub pracy.',
          },
        ],
      },
    ],
  },
]
